import { useState } from 'react'
import type { StepProps, Country } from '@/types/apply'

interface StepAddressProps extends StepProps {
  countries: Country[]
}

export default function StepAddress({ formData, updateField, errors, clearError, countries }: StepAddressProps) {
  const a = formData.address
  const [countrySearch, setCountrySearch] = useState('')

  const filteredCountries = countrySearch
    ? countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
    : countries

  function handleCountryChange(countryName: string) {
    updateField('address', 'country', countryName)
    clearError('country')

    // Sync phone country code in step 1
    const country = countries.find(c => c.name === countryName)
    if (country) {
      updateField('personal_info', 'phone_iso_code', country.code)
      updateField('personal_info', 'phone_dial_code', country.dial_code)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <label className="apply-label">Street Address</label>
        <input
          type="text"
          className={`apply-input ${errors.street_address ? 'apply-input-error' : ''}`}
          placeholder="123 Main Street"
          value={a.street_address}
          onChange={e => { updateField('address', 'street_address', e.target.value); clearError('street_address') }}
          autoFocus
        />
        {errors.street_address && <p className="apply-error-text">{errors.street_address}</p>}
      </div>

      <div>
        <label className="apply-label">
          Address Line 2 <span className="text-white/30">(optional)</span>
        </label>
        <input
          type="text"
          className="apply-input"
          placeholder="Apt, Suite, Unit"
          value={a.address_line_2}
          onChange={e => updateField('address', 'address_line_2', e.target.value)}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="apply-label">City</label>
          <input
            type="text"
            className={`apply-input ${errors.city ? 'apply-input-error' : ''}`}
            placeholder="City"
            value={a.city}
            onChange={e => { updateField('address', 'city', e.target.value); clearError('city') }}
          />
          {errors.city && <p className="apply-error-text">{errors.city}</p>}
        </div>
        <div>
          <label className="apply-label">State / Province</label>
          <input
            type="text"
            className={`apply-input ${errors.state ? 'apply-input-error' : ''}`}
            placeholder="State"
            value={a.state}
            onChange={e => { updateField('address', 'state', e.target.value); clearError('state') }}
          />
          {errors.state && <p className="apply-error-text">{errors.state}</p>}
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="apply-label">Postal Code</label>
          <input
            type="text"
            className={`apply-input ${errors.postal_code ? 'apply-input-error' : ''}`}
            placeholder="10001"
            value={a.postal_code}
            onChange={e => { updateField('address', 'postal_code', e.target.value); clearError('postal_code') }}
          />
          {errors.postal_code && <p className="apply-error-text">{errors.postal_code}</p>}
        </div>
        <div>
          <label className="apply-label">Country</label>
          <div className="relative">
            <input
              type="text"
              className={`apply-input ${errors.country ? 'apply-input-error' : ''}`}
              placeholder="Search country..."
              value={a.country || countrySearch}
              onChange={e => {
                setCountrySearch(e.target.value)
                if (a.country) updateField('address', 'country', '')
              }}
              onFocus={() => { if (a.country) { setCountrySearch(a.country); updateField('address', 'country', '') } }}
            />
            {countrySearch && !a.country && filteredCountries.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-[#111]">
                {filteredCountries.map(c => (
                  <button
                    key={c.code}
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white/80 hover:bg-white/[0.06]"
                    onClick={() => { handleCountryChange(c.name); setCountrySearch('') }}
                  >
                    <span>{c.flag}</span>
                    <span>{c.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {errors.country && <p className="apply-error-text">{errors.country}</p>}
        </div>
      </div>
    </div>
  )
}
